/**
 * "The day" on the Day timeline (user, 19/10/2026: "make it really
 * professional — and expand like the lists in use"): folded into one
 * button that says when the day sends and its busiest hour; open, a column
 * chart of the day by the hour in Danish time — drops or SMS, stacked by
 * container in its colour, the closed hours muted — with a legend and a
 * hover card per hour (local time, containers, lists).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';

import { SegmentedPills } from '@/components/ui';

import type { DayPlanPreview } from '../../../drop-generator.types';
import { instantDk } from '../../../utils/clock/dk-label';
import { Collapse } from '../section/Collapse';
import { SectionToggle } from '../section/SectionToggle';
import { liveDrop, timelineFacts } from '../summary/timeline-facts';
import { dayHours, type HourBucket } from './day-hours';
import { DayPlot } from './DayPlot';
import type { DayMetric } from './HourColumn';

const METRICS = [
  { value: 'drops', label: 'Drops' },
  { value: 'sms', label: 'SMS' },
] as const;

/** "06:00 → 17:30 🇩🇰 · busiest 09:00 (6 drops)". */
function headline(preview: DayPlanPreview, hours: HourBucket[]): string {
  const f = timelineFacts(preview);
  const busiest = hours.reduce((best, h) => (h.drops > best.drops ? h : best), hours[0]);
  const span = f.firstAt && f.lastAt ? `${instantDk(f.firstAt).label} → ${instantDk(f.lastAt).label} 🇩🇰` : '';
  return [span, `busiest ${instantDk(busiest.startIso).label} (${busiest.drops} drop${busiest.drops === 1 ? '' : 's'})`].filter(Boolean).join(' · ');
}

const Legend: FC<{ preview: DayPlanPreview }> = ({ preview }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
    {preview.containers
      .filter((c) => c.drops.some(liveDrop))
      .map((c) => {
        const drops = c.drops.filter(liveDrop);
        return (
          <span key={c.containerId} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: c.color }} aria-hidden />
            <span className="font-medium">{c.name}</span>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {drops.length} · {drops.reduce((s, d) => s + d.amount, 0).toLocaleString('en-US')} SMS
            </span>
          </span>
        );
      })}
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span className="h-2.5 w-2.5 rounded-[3px] bg-muted" aria-hidden />
      window closed
    </span>
  </div>
);

export const DayChart: FC<{ preview: DayPlanPreview }> = ({ preview }) => {
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState<DayMetric>('drops');
  const hours = useMemo(() => dayHours(preview), [preview]);
  const active = hours.filter((h) => h.drops > 0).length;
  if (active === 0) return null;
  return (
    <div className="border-b border-border/40" data-testid="day-chart">
      <SectionToggle icon={BarChart3} title="The day" badge={`${active} h`} summary={headline(preview, hours)} open={open} onToggle={() => setOpen((v) => !v)} />
      <Collapse open={open}>
        <div className="flex flex-col gap-3 px-5 pb-4 pt-1">
          <div className="flex flex-wrap items-center gap-3">
            <Legend preview={preview} />
            <span className="ml-auto">
              <SegmentedPills label="Show" options={METRICS} value={metric} onChange={setMetric} />
            </span>
          </div>
          <DayPlot hours={hours} metric={metric} />
          <p className="text-[11px] text-muted-foreground">Hours in Danish time, from the window&apos;s opening. Hover an hour for its local time, containers and lists.</p>
        </div>
      </Collapse>
    </div>
  );
};
